// src/pages/AdminEventsPage.tsx
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { authApi } from "../lib/auth-api";

const EVENT_TYPES = [
  "", "signup_whitelist", "signup_pending", "verify",
  "login", "login_failed", "logout",
  "approve", "reject", "disable", "enable", "delete",
  "make_admin", "revoke_admin",
  "reset_request", "reset_done", "data_download",
];

export function AdminEventsPage() {
  const [eventType, setEventType] = useState("");
  const [email, setEmail] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo]     = useState("");
  const [limit, setLimit] = useState(200);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "events", eventType, email, from, to, limit],
    queryFn: () => authApi.adminEvents({
      event_type: eventType || undefined,
      email: email || undefined,
      from: from || undefined,
      to: to || undefined,
      limit,
    }),
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center justify-between">
          <h1 className="text-base font-semibold">監査ログ</h1>
          <div className="text-xs space-x-3">
            <Link to="/" className="text-blue-700 hover:underline">ダッシュボードへ</Link>
            <Link to="/admin/users" className="text-blue-700 hover:underline">ユーザ管理</Link>
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-4 py-4">

        <section className="mb-3 grid grid-cols-5 gap-2 text-xs">
          <label className="block">
            <span className="text-slate-600">種別</span>
            <select value={eventType} onChange={(e) => setEventType(e.target.value)} className="auth-input mt-0.5">
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{t || "(全て)"}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-slate-600">Email like</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} className="auth-input mt-0.5" />
          </label>
          <label className="block">
            <span className="text-slate-600">From (YYYY-MM-DD)</span>
            <input value={from} onChange={(e) => setFrom(e.target.value)} className="auth-input mt-0.5" placeholder="2026-05-01" />
          </label>
          <label className="block">
            <span className="text-slate-600">To</span>
            <input value={to} onChange={(e) => setTo(e.target.value)} className="auth-input mt-0.5" />
          </label>
          <label className="block">
            <span className="text-slate-600">Limit</span>
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="auth-input mt-0.5">
              {[50, 100, 200, 500, 1000].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </section>

        {isLoading && <div className="text-slate-400 text-sm">読み込み中…</div>}
        {error && <div className="auth-error">{(error as Error).message}</div>}

        {data && (
          <>
            <div className="text-xs text-slate-500 mb-2">
              該当 {data.total.toLocaleString()} 件中 {data.rows.length} 件表示
              <button onClick={() => refetch()} className="ml-3 text-blue-700 hover:underline">再読み込み</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-100 text-left">
                    <th className="px-2 py-1 border border-slate-200">ID</th>
                    <th className="px-2 py-1 border border-slate-200">日時 (UTC)</th>
                    <th className="px-2 py-1 border border-slate-200">種別</th>
                    <th className="px-2 py-1 border border-slate-200">Email</th>
                    <th className="px-2 py-1 border border-slate-200">IP</th>
                    <th className="px-2 py-1 border border-slate-200">UA</th>
                    <th className="px-2 py-1 border border-slate-200">metadata</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.id}>
                      <td className="px-2 py-1 border border-slate-200">{r.id}</td>
                      <td className="px-2 py-1 border border-slate-200 whitespace-nowrap">{r.ts.slice(0, 19)}</td>
                      <td className="px-2 py-1 border border-slate-200">
                        <span className={
                          r.event_type === "login_failed" ? "text-red-700" :
                          r.event_type === "data_download" ? "text-purple-700" :
                          r.event_type.startsWith("signup") ? "text-blue-700" :
                          ""
                        }>{r.event_type}</span>
                      </td>
                      <td className="px-2 py-1 border border-slate-200">{r.email ?? "-"}</td>
                      <td className="px-2 py-1 border border-slate-200">{r.ip_address ?? "-"}</td>
                      <td className="px-2 py-1 border border-slate-200 max-w-[200px] truncate">{r.user_agent ?? "-"}</td>
                      <td className="px-2 py-1 border border-slate-200 max-w-[300px] truncate font-mono">{r.metadata ?? "-"}</td>
                    </tr>
                  ))}
                  {data.rows.length === 0 && (
                    <tr><td colSpan={7} className="px-2 py-3 text-center text-slate-400 border border-slate-200">
                      該当イベントなし
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
